import React, { useEffect, useRef, useState } from "react";
import {
  CircularProgress,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import CustomNoRowsOverlay from "../../../../newTestPrep/newComponent/TableCustomeComp/CustomNoRowsOverlay";

const STAGE_API =
  "https://sieceducation.in/api/student-information";

const NOTES_API =
  "https://sieceducation.in/api/get-student-enq-notes-by-mobile";

function GradlynkStages({ enquiryData }) {
  const [loading, setLoading] = useState(false);
  const [gradlynkStages, setGradlynkStages] = useState([]);

  const [notesOpen, setNotesOpen] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [notesError, setNotesError] = useState("");

  const [notesPage, setNotesPage] = useState(0);
  const [notesRowsPerPage, setNotesRowsPerPage] = useState(5);
  const [selectedServiceName, setSelectedServiceName] = useState("");

  const stagesControllerRef = useRef(null);
  const notesControllerRef = useRef(null);

  // Fetch Gradlynk stages
  useEffect(() => {
    const controller = new AbortController();
    stagesControllerRef.current = controller;

    const fetchStages = async () => {
      if (!enquiryData?.mobile) {
        setGradlynkStages([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const response = await fetch(STAGE_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            mobile_country_code:
              enquiryData?.mobile_country_code || "91",
            mobile: enquiryData?.mobile,
            email: enquiryData?.email,
          }),
          signal: controller.signal,
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result?.message || "Failed to fetch stages."
          );
        }

        const stages = result?.data?.service_history || [];

        if (controller.signal.aborted) return;

        setGradlynkStages(
          stages.map((item, index) => ({
            ...item,
            gridId:
              item.id ??
              `${item.service_id ?? ""}-${
                item.sub_service_id ?? ""
              }-${item.created_at ?? ""}-${index}`,
          }))
        );
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching stages:", error);
          setGradlynkStages([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchStages();

    return () => {
      controller.abort();

      if (stagesControllerRef.current === controller) {
        stagesControllerRef.current = null;
      }
    };
  }, [
    enquiryData?.mobile,
    enquiryData?.mobile_country_code,
  ]);

  // Fetch enquiry notes for the selected service
  const handleShowNotes = async (row) => {
    // Cancel any previous notes request
    notesControllerRef.current?.abort();

    setSelectedServiceName(row.service_name || "Service");

    const controller = new AbortController();
    notesControllerRef.current = controller;

    setNotesOpen(true);
    setNotesLoading(true);
    setNotes([]);
    setNotesError("");
    setNotesPage(0);

    const payload = {
      mobile_country_code:
        enquiryData?.mobile_country_code || "91",
      mobile: enquiryData?.mobile,
      email: enquiryData?.email,
      service_id: row.service_id,
      sub_service_id: row.sub_service_id ?? null,
    };

    try {
      const response = await fetch(NOTES_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result?.message || "Failed to fetch enquiry notes."
        );
      }

      // Support common response structures
      const notesData = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result?.data?.notes)
          ? result.data.notes
          : Array.isArray(result?.notes)
            ? result.notes
            : [];

      // Ignore results from aborted or replaced requests
      if (
        controller.signal.aborted ||
        notesControllerRef.current !== controller
      ) {
        return;
      }

      setNotes(notesData);
    } catch (error) {
      if (
        error.name !== "AbortError" &&
        !controller.signal.aborted &&
        notesControllerRef.current === controller
      ) {
        console.error("Error fetching enquiry notes:", error);
        setNotesError(
          error.message || "Unable to fetch enquiry notes."
        );
      }
    } finally {
      if (
        !controller.signal.aborted &&
        notesControllerRef.current === controller
      ) {
        setNotesLoading(false);
        notesControllerRef.current = null;
      }
    }
  };

  // Close modal and cancel active notes request
  const handleCloseNotes = () => {
    notesControllerRef.current?.abort();
    notesControllerRef.current = null;

    setNotesOpen(false);
    setNotesLoading(false);
    setNotesError("");
  };

  // Cancel active requests when component unmounts
  useEffect(() => {
    return () => {
      stagesControllerRef.current?.abort();
      notesControllerRef.current?.abort();
    };
  }, []);

  // Gradlynk services columns
  const columns = [
    {
      field: "service_name",
      headerName: "Service",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "sub_service_name",
      headerName: "Sub Service",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "stage_name",
      headerName: "Stage Name",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "assigned_user_name",
      headerName: "Assigned User",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
      minWidth: 170,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      filterable: false,
      minWidth: 80,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleShowNotes(params.row)}
          title="Show Notes"
          aria-label="Show Notes"
          sx={{
            minWidth: "36px",
            padding: "6px",
          }}
        >
          <StickyNote2OutlinedIcon fontSize="small" />
        </Button>
      ),
    },
  ];

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h4" fontWeight={700}>
            Gradlynk Services
          </Typography>
        </Grid>

        {loading ? (
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 4,
            }}
          >
            <CircularProgress />
          </Grid>
        ) : (
          <Grid item xs={12}>
            <DataGrid
              rows={gradlynkStages}
              columns={columns}
              getRowId={(row) => row.gridId}
              pageSizeOptions={[5, 10, 15]}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 5,
                    page: 0,
                  },
                },
              }}
              disableRowSelectionOnClick
              autoHeight
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
              }}
            />
          </Grid>
        )}
      </Grid>

      {/* Enquiry Notes Modal */}
      <Dialog
        open={notesOpen}
        onClose={handleCloseNotes}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Enquiry Notes - {selectedServiceName}
        </DialogTitle>

        <DialogContent dividers>
          {notesLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={4}
            >
              <CircularProgress />
            </Box>
          ) : notesError ? (
            <Alert severity="error">{notesError}</Alert>
          ) : notes.length === 0 ? (
            <Typography textAlign="center" py={3}>
              No enquiry notes found.
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                width: "100%",
                border: "1px solid #bdbdbd",
                borderRadius: 1,
              }}
            >
              <Table
                size="small"
                sx={{
                  borderCollapse: "collapse",
                  "& th, & td": {
                    border: "1px solid #bdbdbd",
                    padding: "12px",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#2e7d32",
                        color: "#ffffff",
                      }}
                    >
                      Notes
                    </TableCell>

                      <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#2e7d32",
                        color: "#ffffff",
                        whiteSpace: "nowrap",
                        width: "220px",
                      }}
                    >
                      Notes Created By
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#2e7d32",
                        color: "#ffffff",
                        whiteSpace: "nowrap",
                        width: "220px",
                      }}
                    >
                      Created At
                    </TableCell>

                    
                  
                  </TableRow>
                </TableHead>

                <TableBody>
                  {notes
                    .slice(
                      notesPage * notesRowsPerPage,
                      notesPage * notesRowsPerPage +
                        notesRowsPerPage
                    )
                    .map((item, index) => (
                      <TableRow key={item.id ?? index}>
                        <TableCell
                          sx={{
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere",
                            verticalAlign: "top",
                          }}
                        >
                          {item.notes || "-"}
                        </TableCell>
                        
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            verticalAlign: "top",
                          }}
                        >
                          {item.created_by_name || ""}
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: "nowrap",
                            verticalAlign: "top",
                          }}
                        >
                          {item.created_at
                            ? new Date(
                                item.created_at.replace(" ", "T")
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                            : "-"}
                        </TableCell>

                         
                      </TableRow>
                    ))}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                count={notes.length}
                page={notesPage}
                onPageChange={(event, newPage) => {
                  setNotesPage(newPage);
                }}
                rowsPerPage={notesRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setNotesRowsPerPage(
                    parseInt(event.target.value, 10)
                  );
                  setNotesPage(0);
                }}
                rowsPerPageOptions={[5, 10, 15]}
              />
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseNotes}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default GradlynkStages;